import React, { useState } from 'react';
import MainLayout from '../components/layout/MainLayout';
import RecentActivities from '../components/dashboard/RecentActivities';
import KnowledgeGraph from '../components/dashboard/KnowledgeGraph';
import { toast } from 'react-toastify';

const Dashboard = () => {
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [currentDate, setCurrentDate] = useState("November 15");
  const [tasks, setTasks] = useState([
    { id: 1, text: "Review Sarah's pathology report", checked: false },
    { id: 2, text: "Sign off on chemotherapy orders for James", checked: false },
    { id: 3, text: "Update treatment plan for John", checked: false },
  ]);
  const [taskInputOpen, setTaskInputOpen] = useState(false);
  const [newTaskText, setNewTaskText] = useState("");

  // Calendar dates for the dropdown
  const calendarDates = [
    "November 14",
    "November 15",
    "November 16",
    "November 17",
    "November 18",
  ];

  const handleDateSelect = (date) => {
    setCurrentDate(date);
    setCalendarOpen(false);
    toast.info(`Schedule updated for ${date}`);
  };

  const toggleCalendar = () => {
    setCalendarOpen(!calendarOpen);
  };

  const handleAddTask = () => {
    if (taskInputOpen) {
      if (newTaskText.trim()) {
        const newTask = {
          id: tasks.length + 1,
          text: newTaskText,
          checked: false
        };
        setTasks([...tasks, newTask]);
        setNewTaskText("");
        toast.success("New task added");
      }
      setTaskInputOpen(false);
    } else {
      setTaskInputOpen(true);
    }
  };

  const toggleTask = (id) => {
    setTasks(tasks.map(task =>
      task.id === id ? { ...task, checked: !task.checked } : task
    ));
    
    const task = tasks.find(t => t.id === id);
    if (task && !task.checked) {
      toast.success(`Task completed: ${task.text}`);
    }
  };

  const handlePatientAction = (action) => {
    if (action === 'document') {
      toast.info("Navigating to documentation for John Doe");
      // In a real app, this would navigate to the proper patient documentation
    } else if (action === 'timeline') {
      toast.info("Opening timeline for John Doe");
      // In a real app, this would show a timeline modal or navigate to timeline view
    }
  };

  // Sample schedule data
  const scheduleData = [
    { time: "08:00", patient: "John Doe", type: "Follow-up" },
    { time: "09:15", patient: "Sarah Smith", type: "New Consult" },
    { time: "10:30", patient: "[Open]", type: "" },
    { time: "11:45", patient: "James Wilson", type: "Treatment Review" },
  ];

  return (
    <MainLayout
      activeNavItem="dashboard"
      searchPlaceholder="Search Patients..."
      notifications={5}
    >
      <div className="flex h-full">
        <div className="flex-1 p-6 overflow-auto">
          <div className="bg-white rounded-lg shadow overflow-hidden mb-6">
            {/* Single Schedule Header with Calendar */}
            <div className="bg-blue-500 text-white p-3 flex justify-between items-center">
              <h2 className="text-lg font-medium">Today's Schedule ({currentDate})</h2>
              <div className="relative">
                <button 
                  className="px-3 py-1 bg-blue-600 rounded"
                  onClick={toggleCalendar}
                >
                  Calendar ▼
                </button>
                
                {calendarOpen && (
                  <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg z-10">
                    <div className="p-2 text-gray-800 font-medium border-b">
                      Select Date
                    </div>
                    {calendarDates.map((date) => (
                      <div 
                        key={date}
                        className={`p-2 hover:bg-blue-100 cursor-pointer ${date === currentDate ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-800 font-medium'}`}
                        onClick={() => handleDateSelect(date)}
                      >
                        {date}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            {/* Schedule table with no header (just the content) */}
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Patient</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Appointment Type</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {scheduleData.map((appointment, index) => (
                  <tr key={index} className="hover:bg-gray-50 cursor-pointer">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{appointment.time}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{appointment.patient}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{appointment.type}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg shadow mb-6">
            <div className="p-4 flex justify-between items-center border-b">
              <h2 className="text-lg font-medium text-gray-800">Priority Tasks</h2>
              <button 
                className="text-blue-500 hover:text-blue-700"
                onClick={handleAddTask}
              >
                Add +
              </button>
            </div>
            <div className="p-3">
              {taskInputOpen && (
                <div className="flex items-center p-2 mb-2">
                  <input
                    type="text"
                    value={newTaskText}
                    onChange={(e) => setNewTaskText(e.target.value)}
                    placeholder="Enter new task..."
                    className="flex-1 p-2 border rounded mr-2"
                    autoFocus
                  />
                  <button 
                    className="px-3 py-1 bg-blue-500 text-white rounded"
                    onClick={handleAddTask}
                  >
                    Save
                  </button>
                </div>
              )}
              
              {tasks.map(task => (
                <div key={task.id} className="flex items-center p-2 hover:bg-gray-50 rounded">
                  <input
                    type="checkbox"
                    checked={task.checked}
                    onChange={() => toggleTask(task.id)}
                    className="h-5 w-5 text-blue-600 border-gray-300 rounded mr-3"
                  />
                  <span className={task.checked ? "line-through text-gray-500" : "text-gray-800"}>
                    {task.text}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <RecentActivities />
          <KnowledgeGraph />
        </div>
        <div className="w-80">
          <div className="bg-gray-200 p-4 h-full">
            <h2 className="text-lg font-medium text-gray-700 mb-4">Active Patient:</h2>
           
            <h3 className="text-xl font-medium text-gray-800">John Doe</h3>
            <p className="text-gray-600 mb-4">58, Male</p>
           
            <h4 className="font-medium text-gray-700 mb-1">Diagnosis:</h4>
            <p className="text-gray-800 mb-4">Metastatic Colorectal Cancer</p>
           
            <h4 className="font-medium text-gray-700 mb-1">Last Visit:</h4>
            <p className="text-gray-800 mb-4">Oct 28, 2023</p>
           
            <h4 className="font-medium text-gray-700 mb-1">Next Steps:</h4>
            <ul className="list-disc pl-5 mb-6">
              <li className="text-gray-800">Review CT</li>
              <li className="text-gray-800">Update Tx Plan</li>
              <li className="text-gray-800">Discuss Trial Options</li>
            </ul>
           
            <h4 className="font-medium text-gray-700 mb-2">Quick Actions:</h4>
            <div className="space-y-2">
              <button 
                className="w-full py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                onClick={() => handlePatientAction('document')}
              >
                Document
              </button>
              <button 
                className="w-full py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                onClick={() => handlePatientAction('timeline')}
              >
                Timeline
              </button>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default Dashboard;